import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MenuList, MenuListDivider, ListItem, Input, Typography } from '@clickguard/design-system'
import { AssetDTO } from 'api-models'
import { useAssetStore, useAccountStore } from 'store'
import { useStores } from 'hooks'
import { websiteFaviconUrl } from 'hooks/use-website-favicon'
import { useShallow } from 'zustand/react/shallow'
import { sanitizeURL, setItemsToLocalStorage } from 'utils'
import Globe02 from '@untitled-ui/icons-react/build/esm/Globe02'

const PlusSolid = ({ className }: { className?: string }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="14"
		height="14"
		viewBox="0 0 14 14"
		fill="none"
		className={className}
		aria-hidden="true"
	>
		<path
			d="M7.5 0.833333C7.5 0.373096 7.1269 0 6.66667 0C6.20643 0 5.83333 0.373096 5.83333 0.833333V5.83333H0.833333C0.373096 5.83333 0 6.20643 0 6.66667C0 7.1269 0.373096 7.5 0.833333 7.5H5.83333V12.5C5.83333 12.9602 6.20643 13.3333 6.66667 13.3333C7.1269 13.3333 7.5 12.9602 7.5 12.5V7.5H12.5C12.9602 7.5 13.3333 7.1269 13.3333 6.66667C13.3333 6.20643 12.9602 5.83333 12.5 5.83333H7.5V0.833333Z"
			fill="currentColor"
		/>
	</svg>
)
import { BreadcrumbTrigger } from './breadcrumb-trigger'

const WebsiteFavicon = ({
	src,
	name
}: {
	src?: string | null
	name?: string
}) => {
	const [failed, setFailed] = useState(false)
	const faviconSrc = src || websiteFaviconUrl(name)
	useEffect(() => setFailed(false), [faviconSrc])
	if (failed || !faviconSrc) {
		return (
			<Globe02
				width={16}
				height={16}
				className="shrink-0 text-[var(--text-secondary,#535255)]"
			/>
		)
	}
	return (
		<img
			src={faviconSrc}
			alt=""
			width={16}
			height={16}
			className="shrink-0 rounded-full object-contain"
			onError={() => setFailed(true)}
			onLoad={(e) => {
				if (e.currentTarget.naturalWidth < 32) setFailed(true)
			}}
		/>
	)
}

interface WebsiteSelectorProps {
	asset?: AssetDTO | null
	onWebsiteChange?: () => void
}

export const WebsiteSelector = ({
	asset,
	onWebsiteChange
}: WebsiteSelectorProps) => {
	const { accountStore, userStore } = useStores()
	const { user } = userStore
	const navigate = useNavigate()
	const { pathname } = useLocation()

	const { accountsWithAssets, setAsset, demoMode, setDemoMode } =
		useAssetStore(
			useShallow((s) => ({
				accountsWithAssets: s.accountsWithAssets,
				setAsset: s.setAsset,
				demoMode: s.demoMode,
				setDemoMode: s.setDemoMode
			}))
		)

	const { account } = useAccountStore(
		useShallow((s) => ({ account: s.account }))
	)

	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const containerRef = useRef<HTMLDivElement>(null)

	// Determine permissions
	const userAccess = accountStore.userAccessLevel(user?.id || '')
	const isManagerOrOwner = userAccess === 'OWNER' || userAccess === 'WRITE'

	// Get websites for the currently selected workspace only, sorted alphabetically
	const currentWorkspace = (accountsWithAssets || []).find(
		(acc) => acc.id === account?.id
	)
	const allWebsites = (currentWorkspace?.assets || [])
		.slice()
		.sort((a, b) => a.name.localeCompare(b.name))

	// Filter by search
	const filteredWebsites = searchQuery
		? allWebsites.filter((w) =>
				w.name.toLowerCase().includes(searchQuery.toLowerCase())
		  )
		: allWebsites

	// Click outside to close
	useEffect(() => {
		if (!isOpen) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen])

	const handleSelectWebsite = (website: AssetDTO) => {
		setDemoMode(false)
		setAsset(website)

		const selectedAccount = accountStore.accounts.find(
			(acc) => acc.id === website.account_id
		)
		if (selectedAccount) {
			accountStore.setAccount(selectedAccount)
		}

		setItemsToLocalStorage([
			{ key: 'selected_asset', value: website.id },
			{ key: 'selected_account', value: selectedAccount?.id || '' }
		])

		// Navigate to the same page but with the new asset
		let path = pathname
		path = path.replace(/(\/acc)[^/]+/, `/${website.account_id}`)
		path = path.replace(/(\/ast)[^/]+/, `/${website.id}`)
		navigate(path)

		setIsOpen(false)
		setSearchQuery('')
		onWebsiteChange?.()
	}

	const handleProtectNewWebsite = () => {
		if (account?.id) {
			navigate(`/setup/${account.id}`)
		}
		setIsOpen(false)
	}

	const websiteIcon = <WebsiteFavicon src={asset?.avatar} name={asset?.name} />

	return (
		// Shrinks at a quarter of the rate of the crumbs after it, so on a narrow
		// navbar the site name stays readable longest.
		<div className="relative flex min-w-0 shrink-[0.25]" ref={containerRef}>
			<BreadcrumbTrigger
				icon={websiteIcon}
				label={demoMode ? 'No asset selected' : sanitizeURL(asset?.name) || 'Select website'}
				onClick={() => setIsOpen(!isOpen)}
				isOpen={isOpen}
			/>

			{isOpen && (
				<div className="absolute top-full left-0 mt-1 z-50">
					<MenuList
						className="w-[280px]"
						maxHeight={320}
						role="listbox"
					>
						{/* Header */}
						<div className="px-3 pt-2 pb-1">
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] font-medium text-[var(--text-secondary,#535255)]">
								Websites
							</span>
						</div>

						<div className="px-2 pb-1">
							<Input
								size="sm"
								state="default"
								placeholder="Search websites..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								autoFocus
							/>
						</div>

						{filteredWebsites.length > 0 ? (
							filteredWebsites.map((website) => (
								<ListItem
									key={website.id}
									label={sanitizeURL(website.name) || website.name}
									leadingIdentifier={
										<WebsiteFavicon src={website.avatar} name={website.name} />
									}
									selected={website.id === asset?.id}
									onClick={() => handleSelectWebsite(website)}
								/>
							))
						) : (
							<div className="px-3 py-2">
								<Typography variant="body-sm" color="muted">
									No websites found
								</Typography>
							</div>
						)}

						{isManagerOrOwner && (
							<>
								<MenuListDivider />
								<ListItem
									label="Protect a new website"
									leadingIcon={<PlusSolid className="text-[var(--text-primary,#29282A)]" />}
									onClick={handleProtectNewWebsite}
								/>
							</>
						)}
					</MenuList>
				</div>
			)}
		</div>
	)
}
